import React, { useState, useEffect, useRef } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { monatsEinordnung } from '../lib/utils';
import { findePauseAlsBlock, pauseAlsLueckeAufteilen, auffaelligeTage, sonstigesOhneKommentar, sonstigesMeldung, stundenSummen, deZahl } from '../lib/stundenzettelHilfen';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../contexts/AuthContext';
import { timesheetsAPI, assignmentsAPI, auditAPI } from '../lib/api';
import { WeeklyBreakdown, OvertimeAlert } from '../components/WeeklyBreakdown';
import { SonderstundenSection } from '../components/SonderstundenSection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Clock,
  Calendar,
  Save,
  Send,
  ArrowLeft,
  CheckCircle2,
  X,
  FileText,
  Download,
  Loader2,
  User,
  School,
  Copy,
  Mail,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Info,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { eingabeVerlassen, leinwandAnpassen } from '../lib/unterschriftLeinwand';

const statusOptions = [
  // "Offen" = Werktag, den noch niemand angefasst hat. Bewusst unauffaellig grau:
  // am Monatsanfang ist das der Normalzustand, keine Fehlermeldung.
  { value: 'unbearbeitet', label: 'Offen', color: 'bg-slate-100 text-slate-500' },
  { value: 'gearbeitet', label: 'Gearbeitet', color: 'bg-green-100 text-green-700' },
  { value: 'krank', label: 'Krank', color: 'bg-red-100 text-red-700' },
  { value: 'urlaub', label: 'Urlaub', color: 'bg-blue-100 text-blue-700' },
  { value: 'frei', label: 'Frei', color: 'bg-sky-100 text-sky-700' },
  { value: 'eigenes_kind_krank', label: 'Krank', color: 'bg-red-100 text-red-700' },
  { value: 'betreutes_kind_krank', label: 'Betr. Kind krank', color: 'bg-red-100 text-red-700 font-semibold' },
  { value: 'sonstiges', label: 'Sonstiges', color: 'bg-slate-100 text-slate-700' },
  { value: 'feiertag', label: 'Feiertag', color: 'bg-teal-100 text-teal-700' },
  { value: 'ferien', label: 'Ferien', color: 'bg-purple-100 text-purple-700' },
  { value: 'wochenende', label: 'Wochenende', color: 'bg-slate-100 text-slate-500' },
];

// Stati, deren Stunden als Arbeitszeit zaehlen UND abgerechnet werden.
// "betreutes_kind_krank" zaehlt voll mit (Jugendamt zahlt diese Tage), Tag bleibt markiert.
const countsAsWorked = (status) => status === 'gearbeitet' || status === 'betreutes_kind_krank';

// Tage, in die Zeiten eingetragen werden DUERFEN. "Offen" gehoert dazu, obwohl der
// Status nicht als Arbeit zaehlt - sonst koennte niemand etwas erfassen, ohne vorher
// von Hand auf "Gearbeitet" zu stellen. recalcEntry setzt den Status dann selbst.
const zeitenErfassbar = (status) => countsAsWorked(status) || status === 'unbearbeitet';

// Ein Grund je Tag: in den Ferien gewinnt "Schulferien" vor dem Status.
// SZ2: Pro Tag genau einmal „Krank“. Alte Tage behalten ihren gespeicherten Wert, damit die Unterschrift gültig bleibt.
const statusAuswahl = (aktuell) => statusOptions.filter((o) => {
  if (o.value === 'wochenende' || o.value === 'ferien') return false;
  return aktuell === 'eigenes_kind_krank' ? o.value !== 'krank' : o.value !== 'eigenes_kind_krank';
});

const statusLabel = (wert) => statusOptions.find(o => o.value === wert)?.label || wert;

const hinweisSatz = (h) => {
  const tag = h.date.split('-');
  const wochentag = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][new Date(h.date).getDay()];
  const zahl = Number(h.hours).toFixed(2).replace('.', ',');
  const kopf = `${wochentag}, ${tag[2]}.${tag[1]}.${tag[0]}: ${zahl} Std.`;
  return h.in_ferien
    ? `${kopf} - in den Schulferien`
    : `${kopf} - der Tag steht auf "${statusLabel(h.status)}", diese Stunden werden nicht bezahlt`;
};

const hinweisKurz = (h) => {
  const tag = `${h.date.slice(8, 10)}.${h.date.slice(5, 7)}.`;
  const zahl = Number(h.hours).toFixed(2).replace('.', ',');
  const grund = h.in_ferien ? 'Schulferien' : `Status ${statusLabel(h.status)}`;
  return `${tag} (${zahl} Std., ${grund})`;
};

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const calculateHours = (startTime, endTime) => {
  if (!startTime || !endTime || startTime === endTime) return 0;
  try {
    const [sH, sM] = startTime.split(':').map(Number);
    const [eH, eM] = endTime.split(':').map(Number);
    if (isNaN(sH) || isNaN(sM) || isNaN(eH) || isNaN(eM)) return 0;
    const startMin = sH * 60 + sM;
    const endMin = eH * 60 + eM;
    if (endMin <= startMin) return 0;
    let diff = (endMin - startMin) / 60;
    return Math.round(diff * 100) / 100;
  } catch { return 0; }
};

// ==================== ZEITBLÖCKE (Helper, reine Funktionen) ====================
const MAX_BLOCKS = 3;

const toMin = (t) => { const [h, m] = (t || '').split(':').map(Number); return (h * 60) + (m || 0); };

// Liest die Bloecke eines Eintrags. Altzettel ohne blocks -> aus start/end ableiten.
const withBlocks = (entry) => {
  if (entry.blocks && entry.blocks.length) return entry.blocks;
  if (entry.start_time && entry.end_time)
    return [{ start_time: entry.start_time, end_time: entry.end_time, hours: entry.hours || 0 }];
  return [];
};

// Fuers RENDERN: ein leerer Arbeitstag muss trotzdem eine Eingabezeile zeigen.
const blocksForEdit = (entry) => {
  const bl = withBlocks(entry);
  return bl.length ? bl : [{ start_time: '', end_time: '', hours: 0 }];
};

// Sortierte Kopie - NUR fuer Pruefungen und Anzeigen, nie fuer den State.
const sortedBlocks = (blocks) =>
  blocks.filter(b => b.start_time && b.end_time)
        .slice()
        .sort((a, b) => a.start_time.localeCompare(b.start_time));

// Setzt Bloecke UND die abgeleiteten Spiegelfelder. Einzige Stelle im Frontend,
// an der start_time/end_time/hours eines Eintrags geschrieben werden.
const recalcEntry = (entry, blocks) => {
  const withHours = blocks.map(b => ({
    ...b,
    hours: (b.start_time && b.end_time) ? calculateHours(b.start_time, b.end_time) : 0,
  }));
  const complete = sortedBlocks(withHours);   // nur diese bestimmen die Spiegelfelder
  const hours = Math.round(complete.reduce((s, b) => s + b.hours, 0) * 100) / 100;
  // Wo Zeiten reingeschrieben werden, wird der Status auf "gearbeitet" gesetzt - ein
  // offener Tag mit Stunden wuerde nirgends mitzaehlen. Andere Stati bleiben.
  const status = (entry.status === 'unbearbeitet' && hours > 0) ? 'gearbeitet' : entry.status;
  return {
    ...entry,
    status,
    blocks: withHours,
    start_time: complete.length ? complete[0].start_time : null,
    end_time:   complete.length ? complete[complete.length - 1].end_time : null,
    hours,
  };
};

// Pause vor Block i in EINGABE-Reihenfolge, als "1:55". null wenn nahtlos/unbekannt.
const pauseBefore = (blocks, i) => {
  if (i < 1) return null;
  const prevEnd = blocks[i - 1]?.end_time, start = blocks[i]?.start_time;
  if (!prevEnd || !start) return null;
  const diff = toMin(start) - toMin(prevEnd);
  if (diff <= 0) return null;          // nahtlos ODER unsortiert -> KEIN Hinweis
  return Math.floor(diff / 60) + ':' + String(diff % 60).padStart(2, '0');
};

const AUDIT_ACTION_LABELS = {
  created: 'Angelegt',
  submitted: 'Eingereicht',
  reopened: 'Wieder geöffnet',
  archived: 'Weggeräumt',
  restored: 'Zurückgeholt',
  billed: 'Als abgerechnet markiert',
  teacher_signature_removed: 'Unterschrift der Lehrkraft entfernt',
  lexware_retry: 'Rechnung erneut angestoßen',
};

const formatAuditEvent = (ev) => {
  const label = AUDIT_ACTION_LABELS[ev.action] || ev.action;
  let text = `${label} von ${ev.user_name || 'Unbekannt'}`;
  if (ev.action === 'reopened' && ev.detail) text += `, Grund: ${ev.detail}`;
  if (ev.action === 'billed' && ev.detail) text += ` (${ev.detail})`;
  return text;
};

const TimesheetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isAdminLike } = useAuth();
  const [timesheet, setTimesheet] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureStep, setSignatureStep] = useState(1);
  const [employeeSignature, setEmployeeSignature] = useState({ signature: '', location: '' });
  const [teacherSignature, setTeacherSignature] = useState({ signature: '', location: '', name: '' });
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [fruehDialog, setFruehDialog] = useState(false);
  // Hinweis auf Stunden in Schulferien oder an Tagen, die nicht zählen (Christian, 09.09.2026).
  // Ref, weil der Einreich-Ablauf direkt nach dem Speichern darauf zugreift.
  const [stundenHinweise, setStundenHinweise] = useState([]);
  const stundenHinweiseRef = useRef([]);
  const [ferienHinweisDialog, setFerienHinweisDialog] = useState(false);
  const [metaDialog, setMetaDialog] = useState(false);
  const [metaForm, setMetaForm] = useState({ child_name: '', school_name: '', aktenzeichen: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  const [confirmSaveDialog, setConfirmSaveDialog] = useState({ open: false, days: [] });
  const originalEntriesRef = useRef(null);
  const originalSonderRef = useRef(null);
  const [monthlyTarget, setMonthlyTarget] = useState(0);
  const [weeklyData, setWeeklyData] = useState({ weeks: [], total_billable: 0, has_overtime: false, overtime_weeks: [] });
  const [weeklyHoursBudget, setWeeklyHoursBudget] = useState(0);
  const [teacherEmail, setTeacherEmail] = useState('');
  const [sendingTeacherEmail, setSendingTeacherEmail] = useState(false);
  const [sonderOpen, setSonderOpen] = useState(false);
  const [sonderstunden, setSonderstunden] = useState([]);
  const [pauschale, setPauschale] = useState(null);
  // Pauschale des Jugendamts (unabhaengig vom Monat) - nur fuer den Hinweis bei Sonderstunden.
  const [pauschaleJugendamt, setPauschaleJugendamt] = useState(null);
  const [savingSonder, setSavingSonder] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);

  useEffect(() => {
    if (id && isAdminLike()) {
      auditAPI.get('timesheet', id).then((r) => setAuditEvents(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  const employeeSigRef = useRef(null);
  const teacherSigRef = useRef(null);

  useEffect(() => { fetchData(); }, [id]);

  // Beide Unterschriftsfelder werden getrennt gemountet. Nach dem Mounten (auch beim
  // "Zurück") die im Zustand vorhandene Unterschrift wieder anzeigen - sonst sieht das
  // Feld leer aus, obwohl bereits unterschrieben wurde.
  useEffect(() => {
    if (!showSignatureDialog) return;
    const feld = signatureStep === 1 ? employeeSigRef.current : teacherSigRef.current;
    if (!feld) return;
    const vorhanden = signatureStep === 1 ? employeeSignature.signature : teacherSignature.signature;
    feld.clear();
    if (vorhanden) feld.fromDataURL(vorhanden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSignatureDialog, signatureStep]);

  // US1: Zeichenfläche nur bei echter Größenänderung neu vermessen (Bild bleibt stehen).
  // Tastatur/Browserleisten lösen auf Android "resize" aus, ändern die Fläche aber nicht.
  useEffect(() => {
    if (!showSignatureDialog) return undefined;
    const beiGroesse = () => leinwandAnpassen(signatureStep === 1 ? employeeSigRef.current : teacherSigRef.current);
    window.addEventListener('resize', beiGroesse);
    return () => window.removeEventListener('resize', beiGroesse);
  }, [showSignatureDialog, signatureStep]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [timesheetRes, assignmentsRes] = await Promise.all([
        timesheetsAPI.getOne(id),
        assignmentsAPI.getAll(),
      ]);
      setTimesheet(timesheetRes.data);
      originalEntriesRef.current = JSON.parse(JSON.stringify(timesheetRes.data.entries || []));
      setSonderstunden(timesheetRes.data.sonderstunden || []);
      originalSonderRef.current = timesheetRes.data.sonderstunden || [];
      setPauschale(timesheetRes.data.pauschale || null);
      setPauschaleJugendamt(timesheetRes.data.pauschale_jugendamt || null);
      setStundenHinweise(timesheetRes.data.stunden_hinweise || []);
      stundenHinweiseRef.current = timesheetRes.data.stunden_hinweise || [];
      setAssignment(assignmentsRes.data.find(a => a.id === timesheetRes.data.assignment_id));
      try {
        const targetRes = await assignmentsAPI.getMonthlyTarget(
          timesheetRes.data.assignment_id,
          timesheetRes.data.month,
          timesheetRes.data.year
        );
        setMonthlyTarget(targetRes.data?.monthly_target || 0);
        setWeeklyHoursBudget(targetRes.data?.weekly_hours || 0);
        setWeeklyData({
          weeks: targetRes.data?.weeks || [],
          total_billable: targetRes.data?.total_billable || 0,
          has_overtime: targetRes.data?.has_overtime || false,
          overtime_weeks: targetRes.data?.overtime_weeks || [],
        });
      } catch (e) {
        setMonthlyTarget(0);
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Stundenzettels');
      navigate('/');
    } finally { setLoading(false); }
  };

  const handleBlockChange = (date, idx, field, value) => {
    setTimesheet(ts => ({
      ...ts,
      entries: ts.entries.map(e => {
        if (e.date !== date) return e;
        const next = blocksForEdit(e).map((b, i) =>
          i === idx ? { ...b, [field]: value } : b);
        return recalcEntry(e, next);
      }),
    }));
  };

  const addBlock = (date) => {
    setTimesheet(ts => ({
      ...ts,
      entries: ts.entries.map(e => {
        if (e.date !== date) return e;
        const current = blocksForEdit(e);
        if (current.length >= MAX_BLOCKS) return e;
        return recalcEntry(e, [...current, { start_time: '', end_time: '', hours: 0 }]);
      }),
    }));
  };

  const removeBlock = (date, idx) => {
    setTimesheet(ts => ({
      ...ts,
      entries: ts.entries.map(e => {
        if (e.date !== date) return e;
        const rest = blocksForEdit(e).filter((_, i) => i !== idx);
        return recalcEntry(e, rest.length ? rest
          : [{ start_time: '', end_time: '', hours: 0 }]);
      }),
    }));
  };

  const handleEntryChange = (date, field, value) => {
    const updatedEntries = timesheet.entries.map((entry) => {
      if (entry.date === date) {
        const updated = { ...entry, [field]: value };
        if (field === 'status' && !countsAsWorked(value)) {
          updated.start_time = null;
          updated.end_time = null;
          updated.hours = 0;
          updated.blocks = [];
        }
        return updated;
      }
      return entry;
    });
    setTimesheet({ ...timesheet, entries: updatedEntries });
  };

  // Pause als eigener Block -> in zwei Arbeitsbloecke mit Luecke umbauen. Nur auf Klick.
  const pauseAufteilen = (date) => {
    setTimesheet(ts => ({
      ...ts,
      entries: ts.entries.map(e =>
        e.date !== date ? e : recalcEntry(e, pauseAlsLueckeAufteilen(blocksForEdit(e)))),
    }));
  };

  const saveEntries = async () => {
    if (pauseAlsBlockMelden()) return false;
    try {
      setSaving(true);
      const res = await timesheetsAPI.updateEntries(id, timesheet.entries);
      setStundenHinweise(res.data?.stunden_hinweise || []);
      stundenHinweiseRef.current = res.data?.stunden_hinweise || [];
      originalEntriesRef.current = JSON.parse(JSON.stringify(timesheet.entries));
      toast.success('Gespeichert');
      return true;
    } catch (error) {
      // Absichtlich KEIN throw: keiner der drei Aufrufer fängt ab, ein Wurf würde als
      // unbehandelte Promise-Ablehnung in der Fehlermeldung landen (fehlerMelder.js).
      const detail = fehlertext(error, 'Fehler beim Speichern');
      toast.error(detail);
      zumFehlertagScrollen(detail);
      return false;
    } finally { setSaving(false); }
  };

  // Meldungen des Servers nennen den Tag als 04.05.2026 - danach dorthin scrollen,
  // sonst ist der beanstandete Tag im Monat nicht zu finden.
  const zumFehlertagScrollen = (meldung) => {
    const treffer = String(meldung || '').match(/(\d{2})\.(\d{2})\.(\d{4})/);
    if (!treffer) return;
    const iso = `${treffer[3]}-${treffer[2]}-${treffer[1]}`;
    // Handy- und Tabellenansicht stehen beide im DOM; die ausgeblendete hat kein
    // offsetParent - auf ihr würde scrollIntoView nichts tun.
    const ziel = [`[data-testid="mobile-entry-${iso}"]`, `[data-testid="entry-row-${iso}"]`]
      .map(s => document.querySelector(s))
      .find(el => el && el.offsetParent !== null);
    if (ziel) ziel.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // true = mindestens ein Tag hat die Pause als eigenen Block -> Meldung zeigen, nichts senden.
  // Datum mit Jahr, weil zumFehlertagScrollen nach dd.MM.yyyy sucht.
  const pauseAlsBlockMelden = () => {
    const tage = (timesheet?.entries || [])
      .filter(e => findePauseAlsBlock(withBlocks(e)))
      .map(e => format(new Date(e.date + 'T00:00:00'), 'dd.MM.yyyy'));
    if (tage.length === 0) return false;
    const gezeigt = tage.slice(0, 5).join(', ');
    const rest = tage.length > 5 ? ` und ${tage.length - 5} weitere` : '';
    const einleitung = tage.length === 1
      ? `Am ${gezeigt} ist die Pause als eigener Block eingetragen.`
      : `An diesen Tagen ist die Pause als eigener Block eingetragen: ${gezeigt}${rest}.`;
    toast.error(`${einleitung} Bitte in der Zeile auf „Ja, als Pause aufteilen" klicken oder den Block löschen.`, { id: 'pause-als-block' });
    zumFehlertagScrollen(tage[0]);
    return true;
  };

  const getSuspiciousChanges = () =>
    auffaelligeTage(originalEntriesRef.current, timesheet?.entries, countsAsWorked)
      .map(iso => format(new Date(iso + 'T00:00:00'), 'dd.MM.'));

  const handleSaveClick = () => {
    if (pauseAlsBlockMelden()) return;
    const hits = getSuspiciousChanges();
    if (hits.length > 0) {
      setConfirmSaveDialog({ open: true, days: hits });
    } else {
      saveEntries();
    }
  };

  const doConfirmedSave = () => {
    setConfirmSaveDialog({ open: false, days: [] });
    saveEntries();
  };

  // Laeuft der Monat des Zettels noch? (Auftrag 06 - Rueckfrage statt Sperre)
  // Nur wenn heute IM Monat des Zettels liegt und VOR dem Monatsletzten. Am Monatsletzten
  // selbst und fuer vergangene Monate ist Einreichen der Normalfall - dann kein Dialog.
  // Backoffice/Admin sehen den Dialog nie.
  const heuteDatum = new Date();
  const letzterMonatstag = timesheet
    ? new Date(timesheet.year, timesheet.month, 0)
    : null;
  const laeuftNoch = !!timesheet && !isAdminLike()
    && heuteDatum.getFullYear() === timesheet.year
    && heuteDatum.getMonth() + 1 === timesheet.month
    && heuteDatum.getDate() < letzterMonatstag.getDate();
  const offeneTage = laeuftNoch ? letzterMonatstag.getDate() - heuteDatum.getDate() : 0;

  const handleStartSubmit = async () => {
    // Pflichtwert bei "Gearbeitet" und "Betr. Kind krank" (Auftrag 03). Nur beim
    // EINREICHEN - Zwischenspeichern muss jederzeit ohne Meldung moeglich bleiben.
    // Nur fuer THAs (Auftrag 07): Backoffice und Admin raeumen fremde Zettel auf und
    // duerfen einreichen - das Backend laesst sie ebenfalls durch.
    const ohneWert = isAdminLike() ? [] : (timesheet?.entries || [])
      .filter(e => countsAsWorked(e.status) && !((e.hours || 0) > 0))
      .map(e => format(new Date(e.date + 'T00:00:00'), 'dd.MM.'));
    if (ohneWert.length > 0) {
      const gezeigt = ohneWert.slice(0, 5).join(', ');
      const rest = ohneWert.length > 5 ? ` und ${ohneWert.length - 5} weitere` : '';
      toast.error(`An diesen Tagen fehlt der Wert: ${gezeigt}${rest} – Bitte Wert eintragen oder den Status ändern.`, { id: 'wert-fehlt' });
      return;
    }
    if (pauseAlsBlockMelden()) return;
    const missing = (timesheet?.entries || [])
      .filter(e => needsBreakEntry(e) && !(e.comment || '').trim())
      .map(e => format(new Date(e.date + 'T00:00:00'), 'dd.MM.'));
    if (missing.length > 0) {
      const shown = missing.slice(0, 5).join(', ');
      const rest = missing.length > 5 ? ` und ${missing.length - 5} weitere` : '';
      toast.error(`An diesen Tagen fehlt die Pause (mindestens 30 Minuten): ${shown}${rest} – bitte beim ersten Block das Ende auf den tatsächlichen Pausenbeginn setzen und über das Plus-Symbol einen zweiten Block ab Pausenende anlegen (z. B. 08:10–12:00 und 12:30–14:50) oder einen Kommentar eintragen.`, { id: 'pause-fehlt' });
      return;
    }
    // Pflichtkommentar bei "Sonstiges" - gilt fuer alle Rollen, auch Backoffice/Admin.
    const sonderOhneKommentar = sonstigesOhneKommentar(sonderstunden);
    if (sonderOhneKommentar.length > 0) {
      setSonderOpen(true);
      toast.error(sonstigesMeldung(sonderOhneKommentar), { id: 'sonder-kommentar' });
      return;
    }
    // Sonderstunden haben ihren eigenen Speichern-Knopf und werden beim Einreichen NICHT
    // mitgeschickt - ungespeicherte Zeilen wären nach dem Einreichen unwiederbringlich weg.
    if (sonderstundenUngespeichert()) {
      setSonderOpen(true);
      toast.error('Sie haben Sonderstunden eingetragen, aber nicht gespeichert. Bitte erst über den Speichern-Knopf im Bereich Sonderstunden sichern.', { id: 'sonder-ungespeichert' });
      return;
    }
    // Rueckfrage beim Einreichen mitten im laufenden Monat (Auftrag 06). Keine Sperre:
    // nur ein Hinweis, weil danach nichts mehr eingetragen werden kann.
    if (laeuftNoch) {
      setFruehDialog(true);
      return;
    }
    await weiterZumUnterschreiben();
  };

  const weiterZumUnterschreiben = async () => {
    // Kein Einreichen auf einem alten Stand: schlägt das Speichern fehl (z. B. überlappende
    // Zeitblöcke), würde das Backend Einträge, PDF und Rechnung aus dem Datenbankstand lesen.
    const gespeichert = await saveEntries();
    if (!gespeichert) return;
    // Nachfrage auf dem GESPEICHERTEN Stand, nie gleichzeitig mit dem Frueh-Dialog.
    if ((stundenHinweiseRef.current || []).length > 0) {
      setFerienHinweisDialog(true);
      return;
    }
    unterschriftOeffnen();
  };

  const unterschriftOeffnen = () => {
    setShowSignatureDialog(true);
    setSignatureStep(1);
    setEmployeeSignature({ signature: '', location: '' });
    setTeacherSignature({ signature: '', location: '', name: '' });
  };

  const clearSignature = (step) => {
    if (step === 1 && employeeSigRef.current) { employeeSigRef.current.clear(); setEmployeeSignature(p => ({ ...p, signature: '' })); }
    if (step === 2 && teacherSigRef.current) { teacherSigRef.current.clear(); setTeacherSignature(p => ({ ...p, signature: '' })); }
  };

  const handleSignatureEnd = (step) => {
    if (step === 1 && employeeSigRef.current) setEmployeeSignature(p => ({ ...p, signature: employeeSigRef.current.toDataURL() }));
    if (step === 2 && teacherSigRef.current) setTeacherSignature(p => ({ ...p, signature: teacherSigRef.current.toDataURL() }));
  };

  const proceedToTeacherSignature = () => {
    if (!employeeSignature.signature || !employeeSignature.location) {
      toast.error('Bitte Unterschrift und Ort eingeben');
      return;
    }
    if (timesheet?.teacher_confirmed) {
      setShowSubmitConfirm(true);
      return;
    }
    // Auch den Zustand leeren, nicht nur die Leinwand: sonst wird beim Vor-Zurück-Vor eine
    // alte Lehrer-Unterschrift eingereicht, obwohl das Feld leer aussieht.
    setTeacherSignature(p => ({ ...p, signature: '' }));
    setSignatureStep(2);
  };

  const submitTimesheet = async () => {
    const confirmed = !!timesheet?.teacher_confirmed;
    if (!confirmed && (!teacherSignature.signature || !teacherSignature.location || !teacherSignature.name)) {
      toast.error('Bitte Name, Unterschrift und Ort eingeben');
      return;
    }
    const currentDate = format(new Date(), 'dd.MM.yyyy');
    // Backend-Grenze (SignatureData.signature): 700.000 Zeichen. Lieber eine klare
    // Meldung als ein HTTP 422, dessen Fehlerobjekt niemand lesen kann.
    const zuGross = [
      ['Ihre Unterschrift', employeeSignature.signature],
      ['Die Unterschrift der Lehrkraft', confirmed ? '' : teacherSignature.signature],
    ].find(([, sig]) => (sig || '').length > 700000);
    if (zuGross) {
      toast.error(`${zuGross[0]} ist zu groß zum Speichern. Bitte im Unterschriftsfeld auf „Löschen" tippen und etwas kleiner unterschreiben.`);
      return;
    }
    try {
      setSubmitting(true);
      await timesheetsAPI.submit(id, {
        employee_signature: { signature: employeeSignature.signature, location: employeeSignature.location, date: currentDate },
        teacher_signature: confirmed
          ? { signature: '', location: '', date: '', name: '' }
          : { signature: teacherSignature.signature, location: teacherSignature.location, date: currentDate, name: teacherSignature.name },
      });
      toast.success('Stundenzettel eingereicht!');
      setShowSignatureDialog(false);
      setShowSubmitConfirm(false);
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Einreichen'));
      fetchData();
    } finally { setSubmitting(false); }
  };

  const handleMetaSave = async () => {
    if (!metaForm.child_name.trim()) {
      toast.error('Bitte mindestens den Namen des Kindes angeben');
      return;
    }
    try {
      setMetaSaving(true);
      await timesheetsAPI.updateMeta(timesheet.id, {
        child_name: metaForm.child_name.trim(),
        school_name: metaForm.school_name.trim(),
        aktenzeichen: metaForm.aktenzeichen.trim(),
      });
      toast.success('Daten nachgetragen');
      setMetaDialog(false);
      fetchData();
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Speichern'));
    } finally {
      setMetaSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await timesheetsAPI.getPDF(id);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf}`;
      link.download = response.data.filename;
      link.click();
      toast.success('PDF heruntergeladen');
    } catch { toast.error('Fehler beim PDF-Download'); }
  };

  const downloadAbrechnungPDF = async () => {
    try {
      const response = await timesheetsAPI.getAbrechnungPDF(id);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf}`;
      link.download = response.data.filename;
      link.click();
      toast.success('Abrechnungs-PDF heruntergeladen');
    } catch { toast.error('Fehler beim PDF-Download'); }
  };

  const calculateStats = () => {
    if (!timesheet?.entries) return { hours: 0, childSickDays: 0, workDays: 0 };
    let hours = 0, childSickDays = 0, workDays = 0;
    timesheet.entries.forEach((e) => {
      if (e.status === 'gearbeitet') { hours += e.hours || 0; if (e.hours > 0) workDays++; }
      if (e.status === 'betreutes_kind_krank') { childSickDays++; hours += e.hours || 0; }
    });
    return { hours, childSickDays, workDays };
  };

  const getStatusColor = (status) => statusOptions.find(o => o.value === status)?.color || 'bg-slate-100 text-slate-700';
  const getDayName = (dateStr) => dayNames[new Date(dateStr).getDay()];
  const isWeekendOrHoliday = (entry) => entry.status === 'wochenende' || entry.status === 'ferien';

  // Zukunft nicht vorschreiben (Auftrag 04). Datum LOKAL vergleichen, nicht in UTC -
  // sonst waere der heutige Tag am Abend schon gesperrt. Nachtragen bleibt unbegrenzt
  // moeglich, gesperrt ist nur alles NACH heute. Backoffice/Admin duerfen weiter alles.
  const heuteISO = format(new Date(), 'yyyy-MM-dd');
  const istZukunft = (entry) => entry.date > heuteISO;
  // Auftrag SZ1: vom Buero eingetragene Tage sind fuer die THA gesperrt.
  const istGesperrt = (entry) => (istZukunft(entry) || !!entry.buero) && !isAdminLike();
  const bueroTitel = (b) => `Von ${b.name} am ${b.am ? format(new Date(b.am), 'dd.MM.yyyy') : '-'} eingetragen – für die THA gesperrt`;

  const getRowBg = (entry) => {
    if (isWeekendOrHoliday(entry)) return 'bg-slate-100';
    switch (entry.status) {
      case 'unbearbeitet': return 'bg-slate-50';
      case 'gearbeitet': return 'bg-green-50';
      case 'krank': return 'bg-red-100';
      case 'urlaub': return 'bg-blue-100';
      case 'frei': return 'bg-sky-50';
      case 'eigenes_kind_krank': return 'bg-red-100';
      case 'feiertag': return 'bg-slate-100';
      case 'betreutes_kind_krank': return 'bg-red-200';
      case 'sonstiges': return 'bg-purple-50';
      default: return '';
    }
  };

  // Pausenpflichtiger Tag: gearbeitet, ueber 6 h, Pausensumme unter 30 min.
  const needsBreakEntry = (entry) => {
    if (!countsAsWorked(entry.status) || (entry.hours || 0) <= 6) return false;
    const sorted = sortedBlocks(withBlocks(entry));
    const breakMinutes = sorted.reduce((sum, b, i) => {
      if (i === 0) return sum;
      const diff = toMin(b.start_time) - toMin(sorted[i - 1].end_time);
      return sum + (diff > 0 ? diff : 0);
    }, 0);
    return breakMinutes < 30;
  };

  const getEntryWarnings = (entry) => {
    const w = [];
    const bl = withBlocks(entry);

    // Pflichtwert: "Gearbeitet" und "Betr. Kind krank" brauchen Stunden (Auftrag 03).
    if (countsAsWorked(entry.status) && !((entry.hours || 0) > 0)) {
      w.push('Bitte Wert eintragen');
    }

    // Ende vor Start - pro Block statt auf den Spiegelfeldern
    if (countsAsWorked(entry.status)) {
      bl.forEach((b, i) => {
        if (b.start_time && b.end_time && toMin(b.end_time) <= toMin(b.start_time)) {
          w.push(bl.length > 1
            ? `Block ${i + 1}: Die Endzeit liegt vor der Startzeit.`
            : 'Die Endzeit liegt vor der Startzeit.');
        }
      });
    }

    // Ueberlappung - auf einer SORTIERTEN Kopie pruefen. Pause als eigener Block bekommt eine
    // eigene Meldung; den Knopf zum Aufteilen rendern Tabelle und Mobilansicht.
    const pauseBlock = findePauseAlsBlock(bl);
    let ueberschneidung = !!pauseBlock;
    if (pauseBlock) {
      const aussen = bl[pauseBlock.aussen];
      const innen = bl[pauseBlock.innen];
      w.push(`Der Block ${innen.start_time}–${innen.end_time} liegt mitten im Block ${aussen.start_time}–${aussen.end_time}. Ist das die Pause? Die Pause wird nicht eingetragen – sie ist die Lücke zwischen zwei Blöcken.`);
    } else {
      const sorted = sortedBlocks(bl);
      for (let i = 1; i < sorted.length; i++) {
        if (toMin(sorted[i].start_time) < toMin(sorted[i - 1].end_time)) {
          ueberschneidung = true;
          w.push(`Zwei Zeitblöcke überschneiden sich (ab ${sorted[i].start_time}). Bitte beim früheren Block das Ende korrigieren (z. B. auf den Pausenbeginn).`);
        }
      }
    }
    // Pausenhinweis nur, wenn es nicht schon eine Ueberschneidung zu beheben gibt.
    if (!ueberschneidung && needsBreakEntry(entry) && !(entry.comment || '').trim()) {
      w.push(sortedBlocks(bl).length >= 2
        ? 'Über 6 Stunden, aber zwischen den Blöcken liegen weniger als 30 Minuten Pause – bitte die Zeiten so anpassen, dass die tatsächliche Pause dazwischen frei bleibt, oder einen Kommentar eintragen.'
        : 'Über 6 Stunden ohne Pause – bitte beim Block das Ende auf den tatsächlichen Pausenbeginn setzen und über das Plus-Symbol einen zweiten Block ab Pausenende anlegen, z. B. 08:10–12:00 und 12:30–14:50. Die Pause selbst wird nicht eingetragen. Alternativ einen Kommentar eintragen.');
    }

    if ((entry.hours || 0) > 10) {
      w.push('Ungewöhnlich viele Stunden (über 10 h) — bitte prüfen.');
    }
    const wd = new Date(entry.date).getDay();
    if (entry.status === 'gearbeitet' && (wd === 0 || wd === 6) && !(entry.comment || '').trim()) {
      w.push('Arbeit am Wochenende — bitte einen Kommentar ergänzen (z. B. Sonderveranstaltung).');
    }
    return w;
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-64" /><Skeleton className="h-96" /></div>;

  // Schlägt das Laden fehl (403/404/401/Netz), leitet fetchData bereits zur Übersicht weiter.
  // Ohne diese Zeile rendert die Seite im Zwischenschritt noch einmal mit timesheet = null
  // und stürzt ab (weisse Seite statt Übersicht).
  if (!timesheet) return (
    <div className="p-6 text-center text-slate-600 space-y-3" data-testid="timesheet-nicht-geladen">
      <p>Der Stundenzettel konnte nicht geladen werden.</p>
      <Button variant="outline" onClick={() => navigate('/')} data-testid="zur-uebersicht-button">
        Zur Übersicht
      </Button>
    </div>
  );

  const stats = calculateStats();
  const isEditable = timesheet?.status === 'offen';
  const canSubmit = timesheet?.status === 'offen';

  // Client-side live weekly breakdown (mirrors backend logic)
  const liveWeekly = (() => {
    if (!timesheet?.entries || !weeklyHoursBudget) return weeklyData;
    const weeksMap = {};
    const m = timesheet.month;
    const y = timesheet.year;
    for (let i = 0; i < timesheet.entries.length; i++) {
      const e = timesheet.entries[i];
      if (!countsAsWorked(e.status) || !e.hours) continue;
      try {
        const d = new Date(e.date + 'T00:00:00');
        // ISO week calculation
        const tmp = new Date(d.getTime());
        tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7));
        const week1 = new Date(tmp.getFullYear(), 0, 4);
        const isoWeek = 1 + Math.round(((tmp - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
        const isoYear = tmp.getFullYear();
        // führende Null: der Server liefert "2026-06"; ohne padStart träfe der Nachschlag
        // für KW 1 bis 9 nie (und sortiert "2026-10" vor "2026-9")
        const key = isoYear + '-' + String(isoWeek).padStart(2, '0');
        if (!weeksMap[key]) weeksMap[key] = { isoYear, isoWeek, hours: 0, dates: [] };
        weeksMap[key].hours += e.hours;
        weeksMap[key].dates.push(d);
      } catch (_) { /* skip */ }
    }
    const budget = weeklyHoursBudget;
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m, 0);
    const keys = Object.keys(weeksMap).sort();
    const weeks = [];
    let totalBillable = 0;
    const overtimeWeeks = [];
    const gekuerzteWochen = [];
    for (let k = 0; k < keys.length; k++) {
      const w = weeksMap[keys[k]];
      const erfasst = Math.round(w.hours * 100) / 100;
      // Obergrenze aus dem Backend: eine Woche über den Monatswechsel bekommt nur den Rest
      const grenzen = timesheet.wochen_obergrenzen || {};
      const obergrenze = grenzen[keys[k]] !== undefined ? grenzen[keys[k]] : budget;
      const anrechenbar = Math.round(Math.min(erfasst, obergrenze) * 100) / 100;
      // Mehrstunden hängen am bewilligten Budget, nicht an der Obergrenze
      const mehrstunden = Math.round(Math.max(0, erfasst - budget) * 100) / 100;
      const isOver = erfasst > budget;
      const gekuerztMonatsgrenze = Math.max(0, Math.round((Math.min(erfasst, budget) - anrechenbar) * 100) / 100);
      const schonVerbraucht = Math.round(Math.max(0, budget - obergrenze) * 100) / 100;
      const minD = w.dates.reduce((a, b) => a < b ? a : b);
      const maxD = w.dates.reduce((a, b) => a > b ? a : b);
      // Monday/Sunday of ISO week
      const mon = new Date(minD.getTime());
      mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
      const sun = new Date(mon.getTime());
      sun.setDate(sun.getDate() + 6);
      const spansMo = mon < monthStart || sun > monthEnd;
      const pad = (n) => String(n).padStart(2, '0');
      const label = 'KW ' + w.isoWeek + ' (' + pad(minD.getDate()) + '.' + pad(minD.getMonth() + 1) + '.–' + pad(maxD.getDate()) + '.' + pad(maxD.getMonth() + 1) + '.)';
      const wk = { iso_year: w.isoYear, iso_week: w.isoWeek, label, erfasst, bewilligt: budget, anrechenbar, mehrstunden, is_over: isOver, spans_months: spansMo, gekuerzt_monatsgrenze: gekuerztMonatsgrenze, schon_verbraucht: schonVerbraucht, rest: Math.round(Math.max(0, obergrenze) * 100) / 100 };
      weeks.push(wk);
      totalBillable += anrechenbar;
      if (isOver) overtimeWeeks.push({ iso_week: w.isoWeek, label, mehrstunden });
      if (gekuerztMonatsgrenze > 0) gekuerzteWochen.push({ iso_week: w.isoWeek, label, gekuerzt_monatsgrenze: gekuerztMonatsgrenze, schon_verbraucht: schonVerbraucht });
    }
    return { weeks, total_billable: Math.round(totalBillable * 100) / 100, has_overtime: overtimeWeeks.length > 0, overtime_weeks: overtimeWeeks, gekuerzte_wochen: gekuerzteWochen };
  })();

  // Group entries by ISO week with date lists (for copy-week feature)
  const weekGroups = (() => {
    if (!timesheet?.entries) return [];
    const map = {};
    for (const e of timesheet.entries) {
      try {
        const d = new Date(e.date + 'T12:00:00');
        const tmp = new Date(d.getTime());
        tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7));
        const week1 = new Date(tmp.getFullYear(), 0, 4);
        const isoWeek = 1 + Math.round(((tmp - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
        const key = tmp.getFullYear() + '-' + isoWeek;
        if (!map[key]) map[key] = { isoWeek, dates: [] };
        map[key].dates.push(e.date);
      } catch (_) { /* skip */ }
    }
    return Object.keys(map).sort().map(k => map[k]);
  })();

  // Kopiert die jeweils VORHERIGE Woche in die Zielwoche (Auftrag 04): vorwaerts zu
  // kopieren wuerde in die Zukunft schreiben. Nur leere Tage werden befuellt, gesetzte
  // Stati (krank, Urlaub, ...) und bereits eingetragene Tage bleiben unberuehrt.
  // Auftrag SW1: schreibt fuer ALLE Rollen nie nach heute, auch fuers Buero nicht.
  const kopiereAusVorwoche = async (targetDates) => {
    if (pauseAlsBlockMelden()) return;
    const byDate = Object.fromEntries(timesheet.entries.map(e => [e.date, e]));
    const newEntries = timesheet.entries.map(e => ({ ...e }));
    const idx = Object.fromEntries(newEntries.map((e, i) => [e.date, i]));
    let copied = 0;
    targetDates.forEach(targetDate => {
      const ti = idx[targetDate];
      if (ti === undefined) return;
      if (istZukunft(newEntries[ti])) return;           // nie in die Zukunft schreiben, fuer niemanden
      if (istGesperrt(newEntries[ti])) return;          // Buero-Tage bleiben fuer die THA unberuehrt
      const d = new Date(targetDate + 'T12:00:00');
      d.setDate(d.getDate() - 7);
      const src = byDate[format(d, 'yyyy-MM-dd')];
      if (!src || !src.start_time || !src.end_time) return;
      const tgt = newEntries[ti];
      // Ziel darf offen ODER gearbeitet-ohne-Stunden sein, sonst kopiert die
      // Wochenkopie ins Leere. Bewusst gesetzte Stati (krank, ...) bleiben unberuehrt.
      if ((tgt.status !== 'gearbeitet' && tgt.status !== 'unbearbeitet') || (tgt.hours || 0) > 0) return;
      const srcBlocks = withBlocks(src);
      if (!srcBlocks.length) return;
      newEntries[ti] = recalcEntry(
        { ...tgt, status: src.status },
        srcBlocks.map(b => ({ ...b, id: undefined }))   // IDs vergibt das Backend neu
      );
      copied += 1;
    });
    if (copied === 0) {
      toast.info('Keine befüllbaren Tage in dieser Woche.');
      return;
    }
    try {
      setSaving(true);
      const res = await timesheetsAPI.updateEntries(id, newEntries);
      setStundenHinweise(res.data?.stunden_hinweise || []);
      stundenHinweiseRef.current = res.data?.stunden_hinweise || [];
      setTimesheet({ ...timesheet, entries: newEntries });
      originalEntriesRef.current = JSON.parse(JSON.stringify(newEntries));
      toast.success(`Woche kopiert (${copied} Tage)`);
    } catch {
      toast.error('Fehler beim Kopieren');
    } finally {
      setSaving(false);
    }
  };

  const sendTeacherConfirmation = async () => {
    if (!teacherEmail.trim()) { toast.error('Bitte E-Mail-Adresse eingeben'); return; }
    try {
      setSendingTeacherEmail(true);
      await timesheetsAPI.requestTeacherConfirmation(id, teacherEmail.trim());
      toast.success('Bestätigungslink versendet');
      setTeacherEmail('');
      const res = await timesheetsAPI.getOne(id);
      setTimesheet(res.data);
    } catch (err) {
      toast.error(fehlertext(err, 'Fehler beim Senden'));
      if (err.response?.status === 409) {
        // Auftrag LK1: schon unterschrieben - Karte sofort aktualisieren
        try {
          const res = await timesheetsAPI.getOne(id);
          setTimesheet(res.data);
        } catch { /* Neuladen ist nur Komfort */ }
      }
    } finally {
      setSendingTeacherEmail(false);
    }
  };

  const saveSonderstunden = async () => {
    const ohneKommentar = sonstigesOhneKommentar(sonderstunden);
    if (ohneKommentar.length > 0) {
      toast.error(sonstigesMeldung(ohneKommentar), { id: 'sonder-kommentar' });
      return;
    }
    try {
      setSavingSonder(true);
      await timesheetsAPI.updateSonderstunden(id, sonderstunden);
      originalSonderRef.current = JSON.parse(JSON.stringify(sonderstunden));
      toast.success('Sonderstunden gespeichert');
    } catch (err) {
      toast.error(fehlertext(err, 'Fehler beim Speichern'));
    } finally {
      setSavingSonder(false);
    }
  };

  // Vergleich unabhängig von Feldreihenfolge und Zahlentyp (0 vs. "0")
  const sonderSchluessel = (liste) => JSON.stringify((liste || []).map(
    e => [e.date || '', Number(e.hours) || 0, e.grund || '', e.comment || '']));

  const sonderstundenUngespeichert = () =>
    sonderSchluessel(originalSonderRef.current) !== sonderSchluessel(sonderstunden);

  const sonderTotal = sonderstunden.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="timesheet-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="mt-0.5 flex-shrink-0" data-testid="back-button">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 font-[Manrope]" translate="no">
              {monthNames[timesheet.month - 1]} {timesheet.year}
            </h1>
            <Badge className={`${timesheet.status === 'eingereicht' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {timesheet.status === 'eingereicht' ? 'Eingereicht' : 'Offen'}
            </Badge>
          </div>
          {monatsEinordnung(timesheet.month, timesheet.year) === 'sonstiges' && (
            <div
              className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              data-testid="falscher-monat-band"
            >
              <strong>Sie erfassen <span translate="no">{monthNames[timesheet.month - 1]} {timesheet.year}</span>.</strong>{' '}
              Heute ist der <span translate="no">{new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>.
            </div>
          )}
          {stundenHinweise.length > 0 && (
            <div
              className="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1"
              data-testid="stunden-hinweis-band"
            >
              <strong>Bitte Stunden prüfen:</strong>{' '}
              <span translate="no">{stundenHinweise.slice(0, 5).map(hinweisKurz).join(', ')}</span>
              {stundenHinweise.length > 5 && (
                <span> und <span translate="no">{stundenHinweise.length - 5}</span> weitere</span>
              )}
              {isEditable && (
                <span className="block mt-0.5">Sie können den Zettel trotzdem einreichen.</span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500 flex-wrap">
            <span className="flex items-center gap-1"><User className="h-3 w-3" />{assignment?.child_name || timesheet.child_name || '—'}</span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-1"><School className="h-3 w-3" />{assignment?.school_name || timesheet.school_name || '—'}</span>
          </div>
          {!assignment && (
            <div
              className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-2 flex-wrap"
              data-testid="orphaned-timesheet-hint"
            >
              <span>
                Hinweis: Die zugehörige Zuordnung existiert nicht mehr.
                {timesheet.child_name
                  ? ' Angezeigt werden die beim Einreichen gespeicherten Daten.'
                  : ' Kind/Schule können von einem Admin nachgetragen werden.'}
              </span>
              {isAdminLike() && (
                <button
                  type="button"
                  onClick={() => {
                    setMetaForm({
                      child_name: timesheet.child_name || '',
                      school_name: timesheet.school_name || '',
                      aktenzeichen: timesheet.aktenzeichen || '',
                    });
                    setMetaDialog(true);
                  }}
                  className="underline font-medium hover:text-amber-900"
                  data-testid="open-meta-backfill-dialog"
                >
                  {timesheet.child_name ? 'Daten korrigieren' : 'Daten nachtragen'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card><CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-1 text-slate-500 text-xs sm:text-sm mb-1"><Clock className="h-3 w-3 sm:h-4 sm:w-4" />Stunden</div>
          <div className="text-xl sm:text-2xl font-bold text-slate-900"><span translate="no">{stats.hours.toFixed(1)}</span><span className="text-xs sm:text-sm font-normal text-slate-500 ml-1" translate="no">/ {monthlyTarget ? monthlyTarget.toFixed(1) : '—'}</span></div>
          {(() => {
            const ist = stats.hours || 0;
            const soll = monthlyTarget || 0;
            if (!soll || soll <= 0) return (
              <div className="mt-2">
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div className="h-2 rounded-full bg-slate-300" style={{ width: '0%' }} />
                </div>
                <p className="text-xs text-slate-400 mt-1"><span translate="no">{ist.toFixed(1)}</span> Std.</p>
              </div>
            );
            const pctRaw = (ist / soll) * 100;
            const dev = Math.abs(ist - soll) / soll * 100;
            const color = dev <= 10 ? 'bg-green-500' : dev <= 20 ? 'bg-yellow-500' : 'bg-red-500';
            const pct = Math.max(0, Math.min(100, pctRaw));
            return (
              <div className="mt-2">
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden" data-testid="hours-progress-bar">
                  <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1"><span translate="no">{ist.toFixed(1)} / {soll.toFixed(1)}</span> Std. (<span translate="no">{Math.round(pctRaw)} %</span>)</p>
              </div>
            );
          })()}
        </CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-1 text-slate-500 text-xs sm:text-sm mb-1"><Calendar className="h-3 w-3 sm:h-4 sm:w-4" />Arbeitstage</div>
          <div className="text-xl sm:text-2xl font-bold text-slate-900" translate="no">{stats.workDays}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-1 text-slate-500 text-xs sm:text-sm mb-1"><FileText className="h-3 w-3 sm:h-4 sm:w-4" />Status</div>
          <div className="text-sm sm:text-lg font-semibold text-slate-900">{timesheet.status === 'eingereicht' ? 'Eingereicht' : 'Offen'}</div>
        </CardContent></Card>
      </div>

      {/* Betr. Kind krank — Schuljahres-Kontingent */}
      {(timesheet.child_sick_days_school_year ?? 0) > 0 && (
        <div className={`text-sm rounded-lg px-3 py-2 ${timesheet.child_sick_days_school_year > (timesheet.sick_days_limit ?? 20) ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-600'}`}>
          Betr. Kind krank (Schuljahr): <span className="font-semibold"><span translate="no">{timesheet.child_sick_days_school_year}</span> von <span translate="no">{timesheet.sick_days_limit ?? 20}</span></span> vom Jugendamt bezahlten Tagen.
          {timesheet.child_sick_days_school_year > (timesheet.sick_days_limit ?? 20) && (
            <span> Tage über dem Limit werden nicht abgerechnet.</span>
          )}
        </div>
      )}

      {/* Overtime Alert */}
      {liveWeekly.has_overtime && <OvertimeAlert />}

      {/* Info hint */}
      <p className="text-xs text-slate-400 px-1">Graue Tage = Wochenende, Ferien oder gesetzlicher Feiertag. An Feiertagen, an denen Sie gearbeitet haben, können Sie den Status auf „Gearbeitet" ändern und Ihre Zeiten eintragen.</p>

      {/* Weekly Breakdown */}
      {liveWeekly.weeks.length > 0 && (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <WeeklyBreakdown weeks={liveWeekly.weeks} totalBillable={liveWeekly.total_billable} hasOvertime={liveWeekly.has_overtime} />
          </CardContent>
        </Card>
      )}

      {/* Copy Week Buttons */}
      {isEditable && weekGroups.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="copy-week-buttons">
          {weekGroups.slice(1).map((wg) => (
            <Button
              key={wg.isoWeek}
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={() => kopiereAusVorwoche(wg.dates)}
              data-testid={`copy-week-${wg.isoWeek}`}
            >
              <Copy className="h-3 w-3 mr-1" />
              KW <span translate="no">{wg.isoWeek}</span> aus Vorwoche füllen
            </Button>
          ))}
        </div>
      )}

      {/* Entries */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3 sm:py-4 px-3 sm:px-6">
          <CardTitle className="text-base sm:text-lg">Tageseinträge</CardTitle>
          {isEditable && (
            <Button onClick={handleSaveClick} disabled={saving} variant="outline" size="sm" data-testid="save-entries-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1 max-sm:hidden">Speichern</span>
            </Button>
          )}
        </CardHeader>
        <CardContent className="px-2 sm:px-6 pb-3 sm:pb-6">
          {/* Desktop Table - hidden on mobile */}
          <div className="max-md:hidden overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Tag</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Datum</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Beginn</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Ende</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Std.</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-slate-500">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {timesheet.entries?.map((entry) => {
                  const gesperrt = istGesperrt(entry);
                  const disabled = isWeekendOrHoliday(entry) || gesperrt;
                  const warnings = getEntryWarnings(entry);
                  const blocks = blocksForEdit(entry);
                  const editable = isEditable && !disabled && zeitenErfassbar(entry.status);
                  const b0 = blocks[0];
                  const showAdd = editable && blocks.length < MAX_BLOCKS && b0.start_time && b0.end_time;
                  const pauseTreffer = editable ? findePauseAlsBlock(blocks) : null;
                  return (
                    <React.Fragment key={entry.date}>
                    <tr className={`border-b border-slate-100 ${gesperrt && istZukunft(entry) ? 'bg-slate-50' : getRowBg(entry)}`} data-testid={`entry-row-${entry.date}`}>
                      <td className="py-2 px-2"><span className={`font-medium ${disabled ? 'text-slate-400' : 'text-slate-600'}`}>{getDayName(entry.date)}</span></td>
                      <td className="py-2 px-2"><span className={`font-medium ${disabled ? 'text-slate-400' : 'text-slate-900'}`} translate="no">{format(new Date(entry.date), 'dd.MM.')}</span>{entry.buero && isAdminLike() && (<Badge className="ml-1 bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0" title={bueroTitel(entry.buero)} data-testid={`buero-badge-${entry.date}`}>Büro</Badge>)}</td>
                      <td className="py-2 px-2">
                        {editable ? (
                          <Input type="time" value={b0.start_time || ''} onChange={(e) => handleBlockChange(entry.date, 0, 'start_time', e.target.value)} className="w-24 h-8 text-center" data-testid={`start-time-input-${entry.date}`} />
                        ) : <span className="text-slate-500" translate="no">{b0.start_time || entry.start_time || '-'}</span>}
                      </td>
                      <td className="py-2 px-2">
                        {editable ? (
                          <Input type="time" value={b0.end_time || ''} onChange={(e) => handleBlockChange(entry.date, 0, 'end_time', e.target.value)} className="w-24 h-8 text-center" data-testid={`end-time-input-${entry.date}`} />
                        ) : <span className="text-slate-500" translate="no">{b0.end_time || entry.end_time || '-'}</span>}
                      </td>
                      <td className="py-2 px-2"><span className={`font-medium ${disabled ? 'text-slate-400' : 'text-slate-900'}`} translate="no">{entry.hours > 0 ? entry.hours.toFixed(2) : '-'}</span></td>
                      <td className="py-2 px-2">
                        {isEditable && !disabled ? (
                          <Select value={entry.status} onValueChange={(v) => handleEntryChange(entry.date, 'status', v)}>
                            <SelectTrigger className="w-40 h-8" data-testid={`status-select-${entry.date}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {statusAuswahl(entry.status).map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : <Badge className={getStatusColor(entry.status)}>{statusOptions.find(o => o.value === entry.status)?.label || entry.status}</Badge>}
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-1">
                          {gesperrt && !isWeekendOrHoliday(entry) && istZukunft(entry) ? (
                            <span className="text-xs text-slate-400 flex-1" data-testid={`future-hint-${entry.date}`}>Noch nicht erfassbar</span>
                          ) : gesperrt && !isWeekendOrHoliday(entry) ? (
                            <span className="text-xs text-slate-500 flex-1 flex items-center gap-1" data-testid={`buero-hint-${entry.date}`}><Lock className="h-3 w-3 shrink-0" />Vom Büro eingetragen{entry.comment ? <span className="text-slate-600">· {entry.comment}</span> : null}</span>
                          ) : isEditable && !disabled ? (
                            <Input value={entry.comment || ''} maxLength={500} onChange={(e) => handleEntryChange(entry.date, 'comment', e.target.value)} placeholder="Kommentar..." className="h-8 flex-1" data-testid={`comment-input-${entry.date}`} />
                          ) : <span className="text-sm text-slate-500 flex-1">{entry.comment || '-'}</span>}
                          {showAdd && (
                            <Button variant="ghost" size="sm" className="h-8 px-2 shrink-0" title="Weiteren Arbeitsblock hinzufügen (z. B. nach der Pause)" onClick={() => addBlock(entry.date)} data-testid={`add-block-${entry.date}`}><Plus className="h-4 w-4" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {blocks.slice(1).map((b, i) => {
                      const pause = pauseBefore(blocks, i + 1);
                      return (
                        <tr key={`${entry.date}-b${i + 1}`} className={`border-b border-slate-100 ${getRowBg(entry)}`} data-testid={`entry-block-row-${entry.date}-${i + 1}`}>
                          <td className="py-1 px-2 pl-4 border-l-2 border-slate-200"></td>
                          <td className="py-1 px-2">{pause && <span className="text-xs italic text-slate-400">Pause <span translate="no">{pause}</span></span>}</td>
                          <td className="py-1 px-2">
                            {editable ? (
                              <Input type="time" value={b.start_time || ''} onChange={(e) => handleBlockChange(entry.date, i + 1, 'start_time', e.target.value)} className="w-24 h-8 text-center" data-testid={`start-time-input-${entry.date}-${i + 1}`} />
                            ) : <span className="text-slate-500" translate="no">{b.start_time || '-'}</span>}
                          </td>
                          <td className="py-1 px-2">
                            {editable ? (
                              <Input type="time" value={b.end_time || ''} onChange={(e) => handleBlockChange(entry.date, i + 1, 'end_time', e.target.value)} className="w-24 h-8 text-center" data-testid={`end-time-input-${entry.date}-${i + 1}`} />
                            ) : <span className="text-slate-500" translate="no">{b.end_time || '-'}</span>}
                          </td>
                          <td className="py-1 px-2"><span className="text-xs text-slate-400" translate="no">{b.hours > 0 ? b.hours.toFixed(2) : '-'}</span></td>
                          <td className="py-1 px-2"></td>
                          <td className="py-1 px-2">
                            {editable && (
                              <Button variant="ghost" size="sm" className="h-8 px-2" title="Zeitblock entfernen" onClick={() => removeBlock(entry.date, i + 1)} data-testid={`remove-block-${entry.date}-${i + 1}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {warnings.length > 0 && (
                      <tr><td colSpan={7} className="px-2 pb-2">
                        <div className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1" data-testid={`entry-warning-${entry.date}`}>
                          {warnings.map((wn, i) => (<div key={i}>{wn}</div>))}
                          {pauseTreffer && (
                            <Button variant="outline" size="sm" className="mt-1 h-auto py-1 whitespace-normal text-left" onClick={() => pauseAufteilen(entry.date)} data-testid={`pause-aufteilen-${entry.date}`}>
                              Ja, als Pause aufteilen: <span translate="no">{pauseTreffer.vorschlag[0].start_time}–{pauseTreffer.vorschlag[0].end_time}</span> und <span translate="no">{pauseTreffer.vorschlag[1].start_time}–{pauseTreffer.vorschlag[1].end_time}</span>
                            </Button>
                          )}
                        </div>
                      </td></tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-1.5">
            {timesheet.entries?.map((entry) => {
              const gesperrt = istGesperrt(entry);
              const disabled = isWeekendOrHoliday(entry) || gesperrt;
              const dateObj = new Date(entry.date);
              const mobileWarnings = getEntryWarnings(entry);
              const mobilPauseTreffer = (isEditable && !disabled && zeitenErfassbar(entry.status))
                ? findePauseAlsBlock(blocksForEdit(entry)) : null;
              return (
                <div
                  key={entry.date}
                  className={`rounded-lg border p-2.5 ${gesperrt && istZukunft(entry) ? 'bg-slate-50' : getRowBg(entry)} ${disabled ? 'opacity-60' : ''}`}
                  data-testid={`mobile-entry-${entry.date}`}
                >
                  {/* Row 1: Day + Date + Hours */}
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${disabled ? 'text-slate-400' : 'text-slate-800'}`}>
                        {getDayName(entry.date)}, <span translate="no">{format(dateObj, 'dd.MM.')}</span>
                      </span>
                      {entry.buero && isAdminLike() && (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0" title={bueroTitel(entry.buero)} data-testid={`mobile-buero-badge-${entry.date}`}>Büro</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {entry.hours > 0 && (
                        <span className="text-sm font-bold text-slate-900" translate="no">{entry.hours.toFixed(2)}h</span>
                      )}
                      <Badge className={`text-xs ${getStatusColor(entry.status)}`}>
                        {statusOptions.find(o => o.value === entry.status)?.label || entry.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Row 2: Editable fields (only for non-disabled, non-weekend) */}
                  {gesperrt && !isWeekendOrHoliday(entry) && istZukunft(entry) && (
                    <p className="text-xs text-slate-400" data-testid={`mobile-future-hint-${entry.date}`}>Noch nicht erfassbar</p>
                  )}
                  {gesperrt && !isWeekendOrHoliday(entry) && !istZukunft(entry) && (
                    <p className="text-xs text-slate-500 flex items-center gap-1" data-testid={`mobile-buero-hint-${entry.date}`}><Lock className="h-3 w-3 shrink-0" />Vom Büro eingetragen{entry.comment ? <span className="text-slate-600">· {entry.comment}</span> : null}</p>
                  )}
                  {isEditable && !disabled && (
                    <div className="space-y-2">
                      {/* Status selector */}
                      <Select value={entry.status} onValueChange={(v) => handleEntryChange(entry.date, 'status', v)}>
                        <SelectTrigger className="h-9 text-sm" data-testid={`mobile-status-${entry.date}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusAuswahl(entry.status).map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Time inputs - Zeitblöcke */}
                      {zeitenErfassbar(entry.status) && (() => {
                        const blocks = blocksForEdit(entry);
                        const last = blocks[blocks.length - 1];
                        const canAdd = blocks.length < MAX_BLOCKS && last.start_time && last.end_time;
                        return (
                          <div className="space-y-2">
                            {blocks.map((b, i) => {
                              const pause = pauseBefore(blocks, i);
                              return (
                                <div key={`${entry.date}-mb${i}`}>
                                  {pause && <div className="text-xs italic text-slate-400 mb-1">Pause <span translate="no">{pause}</span></div>}
                                  <div className="flex gap-2 items-end">
                                    <div className="flex-1">
                                      <label className="text-xs text-slate-500 mb-0.5 block">Von{blocks.length > 1 && (<span> (Block <span translate="no">{i + 1}</span>)</span>)}</label>
                                      <Input type="time" value={b.start_time || ''} onChange={(e) => handleBlockChange(entry.date, i, 'start_time', e.target.value)} className="h-10 text-center text-base" data-testid={i === 0 ? `mobile-start-${entry.date}` : `mobile-start-${entry.date}-${i}`} />
                                    </div>
                                    <div className="flex-1">
                                      <label className="text-xs text-slate-500 mb-0.5 block">Bis</label>
                                      <Input type="time" value={b.end_time || ''} onChange={(e) => handleBlockChange(entry.date, i, 'end_time', e.target.value)} className="h-10 text-center text-base" data-testid={i === 0 ? `mobile-end-${entry.date}` : `mobile-end-${entry.date}-${i}`} />
                                    </div>
                                    {blocks.length > 1 && (
                                      <Button variant="ghost" size="sm" className="h-10 px-2 shrink-0" onClick={() => removeBlock(entry.date, i)} data-testid={`mobile-remove-block-${entry.date}-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {canAdd && (
                              <Button variant="outline" size="sm" className="w-full" onClick={() => addBlock(entry.date)} data-testid={`mobile-add-block-${entry.date}`}><Plus className="h-4 w-4 mr-1" /> Weiterer Arbeitsblock</Button>
                            )}
                          </div>
                        );
                      })()}

                      {/* Comment */}
                      <Input
                        value={entry.comment || ''}
                        maxLength={500}
                        onChange={(e) => handleEntryChange(entry.date, 'comment', e.target.value)}
                        placeholder="Kommentar..."
                        className="h-9 text-sm"
                        data-testid={`mobile-comment-${entry.date}`}
                      />
                    </div>
                  )}
                  {mobileWarnings.length > 0 && (
                    <div className="mt-1.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-1">
                      {mobileWarnings.map((wn, i) => (<div key={i}>{wn}</div>))}
                      {mobilPauseTreffer && (
                        <Button variant="outline" size="sm" className="mt-1 h-auto py-1 whitespace-normal text-left" onClick={() => pauseAufteilen(entry.date)} data-testid={`mobile-pause-aufteilen-${entry.date}`}>
                          Ja, als Pause aufteilen: <span translate="no">{mobilPauseTreffer.vorschlag[0].start_time}–{mobilPauseTreffer.vorschlag[0].end_time}</span> und <span translate="no">{mobilPauseTreffer.vorschlag[1].start_time}–{mobilPauseTreffer.vorschlag[1].end_time}</span>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sonderstunden Section */}
      <SonderstundenSection
        isOpen={sonderOpen}
        onToggle={() => setSonderOpen(o => !o)}
        sonderstunden={sonderstunden}
        onChange={(idx, field, value) => {
          const updated = sonderstunden.map((e, i) => i === idx ? { ...e, [field]: field === 'hours' ? (parseFloat(value) || 0) : value } : e);
          setSonderstunden(updated);
        }}
        onAdd={() => setSonderstunden([...sonderstunden, { date: '', hours: 0, grund: 'Klassenfahrt', comment: '' }])}
        onRemove={(idx) => setSonderstunden(sonderstunden.filter((_, i) => i !== idx))}
        onSave={saveSonderstunden}
        saving={savingSonder}
        disabled={!isEditable}
        total={sonderTotal}
        pauschale={pauschaleJugendamt}
      />

      {pauschale && pauschale.hours > 0 && (
        <Card data-testid="pauschale-card">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium text-slate-900" data-testid="pauschale-label">{pauschale.label}</div>
              <p className="text-xs text-slate-500">Verwaltungsposten des Jugendamts, eigene Rechnungszeile. Nicht in den Arbeitsstunden enthalten.</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-slate-900" data-testid="pauschale-hours" translate="no">
                {Number(pauschale.hours).toFixed(2).replace('.', ',')} h
              </div>
              <div className="text-xs text-slate-500" data-testid="pauschale-gesamt">
                Gesamt inkl. Pauschale:{' '}
                <span translate="no">{(stats.hours + sonderTotal + Number(pauschale.hours)).toFixed(2).replace('.', ',')} h</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Teacher Confirmation Section — nur bei offenen Zetteln: das Anfordern eines neuen
          Links löscht eine vorhandene Unterschrift und ist im Backend auf 'offen' begrenzt. */}
      {(isEditable || timesheet.teacher_confirmed) && (
      <Card data-testid="teacher-confirmation-card">
        <CardHeader className="py-3 sm:py-4 px-3 sm:px-6">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Bestätigung Lehrkraft oder Sekretariat
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-4">
          {timesheet.teacher_confirmed ? (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3" data-testid="teacher-confirmed-status">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                Unterschrieben von {timesheet.teacher_confirmed_name || timesheet.teacher_signature_name}
                {timesheet.teacher_signature_date && (
                  <span> am <span translate="no">{timesheet.teacher_signature_date}</span></span>
                )}
              </span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
              <div className="flex-1 w-full sm:w-auto">
                <Label className="text-sm text-slate-600 mb-1 block">E-Mail der Lehrkraft oder des Sekretariats</Label>
                <Input
                  type="email"
                  value={teacherEmail}
                  onChange={(e) => setTeacherEmail(e.target.value)}
                  placeholder="lehrer@schule.de"
                  className="h-10"
                  data-testid="teacher-email-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={sendTeacherConfirmation}
                disabled={sendingTeacherEmail || !teacherEmail.trim()}
                className="w-full sm:w-auto"
                data-testid="send-teacher-confirmation-button"
              >
                {sendingTeacherEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Bestätigungslink senden
              </Button>
            </div>
          )}
          {!timesheet.teacher_confirmed && (
            <div
              className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mt-3"
              data-testid="hinweis-link-schule"
            >
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Lehrkraft nicht da? Schicken Sie den Link an eine andere Lehrkraft oder an das Sekretariat. Auch eine kranke Lehrkraft kann über den Link von zu Hause aus unterschreiben. Frühere Links bleiben gültig.</span>
            </div>
          )}
          {timesheet.teacher_email && !timesheet.teacher_confirmed && (
            <p className="text-xs text-slate-400 mt-2">Zuletzt gesendet an: {timesheet.teacher_email}</p>
          )}
        </CardContent>
      </Card>
      )}

      {/* KK1: Kind krank über dem Limit (nur Verwaltung, unabhängig vom Lexware-Status) */}
      {isAdminLike() && timesheet.kind_krank_warnung && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="kind-krank-warnung">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Krankheitstage über dem Limit: bitte prüfen</p>
              <p className="text-sm text-amber-700 mt-1">
                <span translate="no">{timesheet.kind_krank_warnung.anzahl}</span> von <span translate="no">{timesheet.kind_krank_warnung.limit}</span> Tagen „Betr. Kind krank“ in eingereichten Stundenzetteln dieses Schuljahres. Tage über dem Limit werden nicht abgerechnet. Der Rechnungsentwurf entsteht trotzdem.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lexware Rechnungsstatus (nur für Verwaltung bei eingereichten Zetteln) */}
      {timesheet.status === 'eingereicht' && isAdminLike() && ['error', 'conflict', 'unchecked'].includes(timesheet.lexware_status) && (() => {
        const st = timesheet.lexware_status;
        const conf = {
          error: {
            box: 'border-red-200 bg-red-50', icon: 'text-red-600', titleCls: 'text-red-800', body: 'text-red-700', hintCls: 'text-red-600', btn: 'text-red-700 border-red-300 hover:bg-red-100',
            title: 'Rechnung konnte nicht automatisch erstellt werden',
            hint: 'Bitte am Einsatz Jugendamt und Qualifikation prüfen und die Rechnung anschließend erneut anstoßen.',
          },
          conflict: {
            box: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', titleCls: 'text-amber-800', body: 'text-amber-700', hintCls: 'text-amber-700', btn: 'text-amber-800 border-amber-300 hover:bg-amber-100',
            title: 'Rechnung nicht erstellt – in Lexware liegt noch ein Beleg',
            hint: 'Bitte den offenen Entwurf zu diesem Zeitraum in Lexware löschen und danach hier erneut anstoßen. Es wurde keine neue Rechnung erstellt.',
          },
          unchecked: {
            box: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', titleCls: 'text-amber-800', body: 'text-amber-700', hintCls: 'text-amber-700', btn: 'text-amber-800 border-amber-300 hover:bg-amber-100',
            title: 'Rechnungsstatus konnte nicht geprüft werden',
            hint: 'Lexware war nicht erreichbar. Bitte in Lexware nachsehen und die Rechnung anschließend erneut anstoßen.',
          },
        }[st];
        return (
        <div className={`rounded-lg border ${conf.box} p-4`} data-testid={`lexware-${st}-box`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={`h-5 w-5 ${conf.icon} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${conf.titleCls}`}>{conf.title}</p>
              {timesheet.lexware_error && <p className={`text-sm ${conf.body} mt-1`}>{timesheet.lexware_error}</p>}
              <p className={`text-xs ${conf.hintCls} mt-2`}>{conf.hint}</p>
              <Button
                variant="outline"
                size="sm"
                className={`mt-3 ${conf.btn}`}
                data-testid="lexware-retry-button"
                onClick={async function retryLexware() {
                  try {
                    // Auftrag LX1: eigener Endpunkt fuers Buero, Antwort { ok, blocker, lexware_status, lexware_error }
                    const res = await timesheetsAPI.lexwareErneut(timesheet.id);
                    const { ok, blocker, lexware_error: fehler } = res.data || {};
                    if (blocker) {
                      toast.error(`Rechnung nicht erstellt: ${blocker}`);
                      return;
                    }
                    if (!ok) {
                      toast.error(`Rechnung nicht erstellt: ${fehler || 'Grund unbekannt, bitte in Lexware nachsehen.'}`);
                      return;
                    }
                    toast.success('Rechnungs-Entwurf in Lexware Office erstellt.');
                    window.location.reload();
                  } catch (e) {
                    toast.error(fehlertext(e, 'Erneuter Versuch fehlgeschlagen.'));
                  }
                }}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Rechnung erneut versuchen
              </Button>
            </div>
          </div>
        </div>
        );
      })()}
      {timesheet.status === 'eingereicht' && isAdminLike() && (timesheet.lexware_status === 'ok' || (!timesheet.lexware_status && timesheet.lexware_invoice_id)) && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="lexware-ok-box">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-800">Rechnungs-Entwurf in Lexware Office erstellt.</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
        {/* Reguläres PDF: für offene UND eingereichte Zettel */}
        <Button variant="outline" onClick={downloadPDF} className="w-full sm:w-auto" data-testid="download-pdf-button">
          <Download className="h-4 w-4 mr-2" />PDF herunterladen
        </Button>
        {/* Abrechnungs-PDF: nur bei eingereichten Zetteln */}
        {timesheet.status === 'eingereicht' && (
          <Button variant="outline" onClick={downloadAbrechnungPDF} className="w-full sm:w-auto" data-testid="download-abrechnung-pdf-button">
            <FileText className="h-4 w-4 mr-2" />Abrechnungs-PDF
          </Button>
        )}
        {isEditable && (
          <>
            <Button variant="outline" onClick={handleSaveClick} disabled={saving} className="w-full sm:w-auto" data-testid="save-button">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Speichern
            </Button>
            {canSubmit && (
              <Button className="bg-blue-800 hover:bg-blue-900 w-full sm:w-auto" onClick={handleStartSubmit} disabled={saving} data-testid="submit-button">
                <Send className="h-4 w-4 mr-2" />Unterschreiben & Einreichen
              </Button>
            )}
          </>
        )}
      </div>

      {/* Signature Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{signatureStep === 1 ? 'Ihre Unterschrift' : 'Unterschrift Lehrkraft oder Sekretariat'}</DialogTitle>
            <DialogDescription>
              {signatureStep === 1 ? 'Bitte unterschreiben Sie als Mitarbeiter/in' : 'Bitte lassen Sie eine Lehrkraft oder das Sekretariat unterschreiben'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {signatureStep === 2 && (
              <div>
                <div
                  className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mb-3"
                  data-testid="hinweis-unterschrift-schule"
                >
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Ihre Lehrkraft ist nicht da? Jede andere Lehrkraft der Schule oder das Sekretariat darf unterschreiben – es muss nicht die Klassenlehrkraft sein.</span>
                </div>
                {(() => {
                  const summen = stundenSummen(timesheet?.entries || [], countsAsWorked);
                  return (
                    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 mb-3 text-sm" data-testid="unterschrift-summen">
                      <div className="flex justify-between gap-2">
                        <span>Anwesenheitsstunden (Kind in der Schule)</span>
                        <span data-testid="unterschrift-summe-anwesend" translate="no">{deZahl(summen.anwesend)}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span>Stunden bei Abwesenheit des Kindes</span>
                        <span data-testid="unterschrift-summe-kind-abwesend" translate="no">{deZahl(summen.kind_abwesend)}</span>
                      </div>
                      <div className="flex justify-between gap-2 font-bold border-t border-gray-300 mt-1.5 pt-1.5">
                        <span>Gesamtstunden</span>
                        <span data-testid="unterschrift-summe-gesamt" translate="no">{deZahl(summen.gesamt)}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1.5">
                        Das sind die erfassten Stunden für{' '}
                        <span translate="no">{monthNames[timesheet.month - 1]} {timesheet.year}</span>.
                      </p>
                    </div>
                  );
                })()}
                <Label>Name Lehrkraft oder Sekretariat *</Label>
                <Input value={teacherSignature.name} onChange={(e) => setTeacherSignature(p => ({ ...p, name: e.target.value }))} placeholder="z.B. Frau Müller" className="h-11" data-testid="teacher-name-input" />
              </div>
            )}
            <div>
              <Label>Ort *</Label>
              <Input
                value={signatureStep === 1 ? employeeSignature.location : teacherSignature.location}
                onChange={(e) => signatureStep === 1 ? setEmployeeSignature(p => ({ ...p, location: e.target.value })) : setTeacherSignature(p => ({ ...p, location: e.target.value }))}
                placeholder="z.B. München" className="h-11" data-testid={`signature-location-${signatureStep}`}
              />
            </div>
            <div>
              <Label>Datum</Label>
              <Input value={format(new Date(), 'dd.MM.yyyy')} disabled className="bg-slate-50 h-11" data-testid={`signature-date-${signatureStep}`} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Unterschrift *</Label>
                <Button variant="ghost" size="sm" onClick={() => clearSignature(signatureStep)} data-testid={`clear-signature-${signatureStep}`}>
                  <X className="h-4 w-4 mr-1" />Löschen
                </Button>
              </div>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-1">
                {/* Zwei getrennte Instanzen, immer nur die des aktuellen Schritts gemountet.
                    Eine geteilte Leinwand mit umgehängtem ref hat die Striche der Lehrkraft
                    als Mitarbeiter-Unterschrift gespeichert. Nicht per CSS verstecken:
                    react-signature-canvas misst offsetWidth beim Mounten. */}
                {signatureStep === 1 ? (
                  <SignatureCanvas
                    key="unterschrift-1"
                    ref={employeeSigRef}
                    clearOnResize={false}
                    canvasProps={{ className: 'w-full bg-white rounded', style: { width: '100%', height: '160px' }, 'data-testid': 'signature-canvas-1' }}
                    onBegin={() => eingabeVerlassen()}
                    onEnd={() => handleSignatureEnd(1)}
                  />
                ) : (
                  <SignatureCanvas
                    key="unterschrift-2"
                    ref={teacherSigRef}
                    clearOnResize={false}
                    canvasProps={{ className: 'w-full bg-white rounded', style: { width: '100%', height: '160px' }, 'data-testid': 'signature-canvas-2' }}
                    onBegin={() => eingabeVerlassen()}
                    onEnd={() => handleSignatureEnd(2)}
                  />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Mit dem Finger oder der Maus unterschreiben</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {signatureStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setShowSignatureDialog(false)} className="w-full sm:w-auto">Abbrechen</Button>
                <Button className="bg-blue-800 hover:bg-blue-900 w-full sm:w-auto" onClick={proceedToTeacherSignature} data-testid="proceed-to-teacher-signature">
                  {timesheet?.teacher_confirmed ? 'Einreichen' : 'Weiter zur Unterschrift Lehrkraft/Sekretariat'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSignatureStep(1)} className="w-full sm:w-auto">Zurück</Button>
                <Button className="bg-blue-800 hover:bg-blue-900 w-full sm:w-auto" onClick={() => setShowSubmitConfirm(true)} disabled={submitting} data-testid="final-submit-button">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Wird eingereicht...</> : <><CheckCircle2 className="h-4 w-4 mr-2" />Einreichen</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Confirmation */}
      <AlertDialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stundenzettel einreichen?</AlertDialogTitle>
            <AlertDialogDescription>
              Nach dem Einreichen kann der Stundenzettel nicht mehr bearbeitet werden.
              {liveWeekly.has_overtime && (
                <span className="block mt-2 text-red-700 font-medium">
                  Hinweis: Es wurden Mehrstunden erkannt. Das Backoffice wird beim Einreichen darüber informiert.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={submitTimesheet} disabled={submitting} className="bg-blue-800 hover:bg-blue-900" data-testid="confirm-submit-button">Ja, einreichen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={ferienHinweisDialog} onOpenChange={setFerienHinweisDialog}>
        <AlertDialogContent className="max-h-[85vh] overflow-y-auto" data-testid="stunden-hinweis-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bitte Stunden prüfen</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block max-h-48 overflow-y-auto font-medium text-slate-700">
                {stundenHinweise.slice(0, 5).map(h => (
                  <span key={h.date} className="block" translate="no">{hinweisSatz(h)}</span>
                ))}
                {stundenHinweise.length > 5 && (
                  <span className="block">und <span translate="no">{stundenHinweise.length - 5}</span> weitere Tage</span>
                )}
              </span>
              <span className="block mt-2">
                In den Schulferien wird normalerweise nicht gearbeitet. Sind die Stunden falsch, gehen Sie
                zurück und korrigieren den Tag. Tage, die Sie nicht ändern können, korrigiert das Büro.
                Stimmen die Stunden, reichen Sie trotzdem ein - das Büro bekommt einen Hinweis.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stunden-hinweis-pruefen">Zurück und prüfen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-800 hover:bg-blue-900"
              data-testid="stunden-hinweis-weiter"
              onClick={() => { setFerienHinweisDialog(false); unterschriftOeffnen(); }}
            >
              Trotzdem einreichen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={fruehDialog} onOpenChange={setFruehDialog}>
        <AlertDialogContent data-testid="frueh-einreichen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Der Monat läuft noch bis zum{' '}
              <span translate="no">{letzterMonatstag ? format(letzterMonatstag, 'dd.MM.yyyy') : ''}</span>.
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sie reichen den Stundenzettel für{' '}
              <span translate="no">{monthNames[timesheet.month - 1]} {timesheet.year}</span> heute,
              am <span translate="no">{format(heuteDatum, 'dd.MM.yyyy')}</span>, ein. Danach können Sie
              nichts mehr eintragen – das Backoffice müsste den Zettel für Sie wieder öffnen.
              <span className="block mt-2 font-medium text-slate-700">
                Es sind noch <span translate="no">{offeneTage}</span>{' '}
                <span>{offeneTage === 1 ? 'Tag' : 'Tage'}</span> offen.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="frueh-abbrechen">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setFruehDialog(false); weiterZumUnterschreiben(); }}
              className="bg-blue-800 hover:bg-blue-900"
              data-testid="frueh-trotzdem-einreichen"
            >
              Trotzdem einreichen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmSaveDialog.open} onOpenChange={function(v) { if (!v) setConfirmSaveDialog({ open: false, days: [] }); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Auffällige Änderung erkannt</AlertDialogTitle>
            <AlertDialogDescription>
              Folgende Tage wurden gelöscht oder stark geändert: <strong translate="no">{confirmSaveDialog.days.join(', ')}</strong>. Trotzdem speichern?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doConfirmedSave} data-testid="confirm-suspicious-save">Ja, speichern</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={metaDialog} onOpenChange={setMetaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kind/Schule nachtragen</DialogTitle>
            <DialogDescription>
              Die ursprüngliche Zuordnung wurde gelöscht. Trage hier die Daten aus der
              Fallakte nach — sie werden fest auf diesem Stundenzettel gespeichert.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="meta-child">Name des Kindes *</Label>
              <Input
                id="meta-child"
                value={metaForm.child_name}
                onChange={(e) => setMetaForm((p) => ({ ...p, child_name: e.target.value }))}
                data-testid="meta-child-input"
              />
            </div>
            <div>
              <Label htmlFor="meta-school">Schule</Label>
              <Input
                id="meta-school"
                value={metaForm.school_name}
                onChange={(e) => setMetaForm((p) => ({ ...p, school_name: e.target.value }))}
                data-testid="meta-school-input"
              />
            </div>
            <div>
              <Label htmlFor="meta-az">Aktenzeichen</Label>
              <Input
                id="meta-az"
                value={metaForm.aktenzeichen}
                onChange={(e) => setMetaForm((p) => ({ ...p, aktenzeichen: e.target.value }))}
                data-testid="meta-az-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMetaDialog(false)}>Abbrechen</Button>
            <Button onClick={handleMetaSave} disabled={metaSaving} data-testid="meta-save-button">
              {metaSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdminLike() && (
        <Card data-testid="audit-log-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Verlauf
            </CardTitle>
          </CardHeader>
          <CardContent>
            {auditEvents.length === 0 ? (
              <p className="text-sm text-slate-500">
                Für diesen Stundenzettel wurden noch keine Änderungen protokolliert.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="audit-log-list">
                {auditEvents.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-3 text-sm border-b border-slate-100 pb-2 last:border-0">
                    <span className="text-xs text-slate-400 whitespace-nowrap tabular-nums pt-0.5" translate="no">
                      {new Date(ev.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                    <span className="text-slate-700">{formatAuditEvent(ev)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TimesheetPage;
